/**
 * Web Audio API Dance Spectrum Visualizer
 * Renders real-time frequency data or ambient wave onto an HTML5 Canvas.
 */

import { showToast } from './utils.ts';

export class AudioVisualizer {
    private audio: HTMLAudioElement;
    private container: HTMLElement;
    private canvas: HTMLCanvasElement;
    private toggleBtn: HTMLButtonElement | null;
    private fullscreenBtn: HTMLButtonElement | null;
    private audioCtx: AudioContext | null = null;
    private analyser: AnalyserNode | null = null;
    private sourceNode: MediaElementAudioSourceNode | null = null;
    private isRunning: boolean = false;
    private animationFrameId: number | null = null;
    private isAudioPlaying: () => boolean;
    private onVisibilityChange: () => void;

    constructor(
        audio: HTMLAudioElement,
        container: HTMLElement,
        canvas: HTMLCanvasElement,
        toggleBtn: HTMLButtonElement | null,
        fullscreenBtn: HTMLButtonElement | null,
        isAudioPlaying: () => boolean,
        onVisibilityChange: () => void
    ) {
        this.audio = audio;
        this.container = container;
        this.canvas = canvas;
        this.toggleBtn = toggleBtn;
        this.fullscreenBtn = fullscreenBtn;
        this.isAudioPlaying = isAudioPlaying;
        this.onVisibilityChange = onVisibilityChange;
    }

    public initAudioContext(): void {
        if (!this.audioCtx) {
            try {
                const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                if (AudioContextClass) {
                    this.audioCtx = new AudioContextClass();
                    this.analyser = this.audioCtx.createAnalyser();
                    this.analyser.fftSize = 128;
                    this.sourceNode = this.audioCtx.createMediaElementSource(this.audio);
                    this.sourceNode.connect(this.analyser);
                    this.analyser.connect(this.audioCtx.destination);
                }
            } catch (e) {
                console.warn('Web Audio API already connected or not supported', e);
            }
        }
        if (this.audioCtx && this.audioCtx.state === 'suspended') {
            this.audioCtx.resume();
        }
        if (this.isRunning) {
            this.startLoop();
        }
    }

    public toggle(force: boolean | null = null): void {
        const shouldOpen = force !== null ? force : !this.container.classList.contains('open');
        this.setVisibility(shouldOpen, true);
        this.onVisibilityChange();
    }

    public restore(isOpen: boolean): void {
        this.setVisibility(isOpen, false);
    }

    private setVisibility(shouldOpen: boolean, announce: boolean): void {
        if (shouldOpen) {
            this.container.classList.add('open');
            this.toggleBtn?.classList.add('active');
            this.fullscreenBtn?.classList.add('active');
            this.isRunning = true;
            this.initAudioContext();
            if (announce) {
                document.querySelector('.main-content')?.scrollTo({ top: 0, behavior: 'smooth' });
                showToast('Audio Visualizer: ON', 'info', 'visualizer');
            }
        } else {
            this.container.classList.remove('open');
            this.toggleBtn?.classList.remove('active');
            this.fullscreenBtn?.classList.remove('active');
            this.isRunning = false;
            if (this.animationFrameId !== null) {
                cancelAnimationFrame(this.animationFrameId);
                this.animationFrameId = null;
            }
            if (announce) showToast('Audio Visualizer: OFF', 'info', 'visualizer');
        }
    }

    public getIsOpen(): boolean {
        return this.container.classList.contains('open');
    }

    private startLoop(): void {
        if (this.animationFrameId !== null) return;

        const canvas = this.canvas;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const render = (): void => {
            if (!this.isRunning) {
                this.animationFrameId = null;
                return;
            }

            const dpr = window.devicePixelRatio || 1;
            const displayWidth = canvas.offsetWidth;
            const displayHeight = canvas.offsetHeight;

            if (canvas.width !== displayWidth * dpr || canvas.height !== displayHeight * dpr) {
                canvas.width = displayWidth * dpr;
                canvas.height = displayHeight * dpr;
            }

            ctx.save();
            ctx.scale(dpr, dpr);
            ctx.clearRect(0, 0, displayWidth, displayHeight);

            // Subtle dark background grid line
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.04)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(0, displayHeight - 1);
            ctx.lineTo(displayWidth, displayHeight - 1);
            ctx.stroke();

            const bufferLength = this.analyser ? this.analyser.frequencyBinCount : 64;
            const dataArray = new Uint8Array(bufferLength);

            let hasRealAudio = false;
            if (this.analyser && this.isAudioPlaying()) {
                this.analyser.getByteFrequencyData(dataArray);
                for (let i = 0; i < bufferLength; i++) {
                    if (dataArray[i] > 5) {
                        hasRealAudio = true;
                        break;
                    }
                }
            }

            const barCount = Math.min(64, bufferLength);
            const totalSpacing = 4;
            const barWidth = Math.max(3, (displayWidth - (barCount * totalSpacing)) / barCount);

            for (let i = 0; i < barCount; i++) {
                let barHeight = 0;

                if (hasRealAudio) {
                    const val = dataArray[i] || 0;
                    barHeight = (val / 255) * (displayHeight - 40) + 4;
                }

                if (barHeight === 0) continue;

                const x = i * (barWidth + totalSpacing) + totalSpacing;
                const y = displayHeight - barHeight;

                // Vibrant Neon Gradient
                const gradient = ctx.createLinearGradient(0, displayHeight, 0, y);
                gradient.addColorStop(0, '#1ed760');
                gradient.addColorStop(0.5, '#00bcd4');
                gradient.addColorStop(1, '#ff007a');

                ctx.fillStyle = gradient;
                ctx.shadowColor = '#1ed760';
                ctx.shadowBlur = hasRealAudio ? 12 : 6;

                // Rounded bar caps with fallback
                ctx.beginPath();
                const r = Math.min(barWidth / 2, 4);
                if (ctx.roundRect) {
                    ctx.roundRect(x, y, barWidth, barHeight, [r, r, 0, 0]);
                } else {
                    ctx.rect(x, y, barWidth, barHeight);
                }
                ctx.fill();
            }

            ctx.restore();
            this.animationFrameId = requestAnimationFrame(render);
        };

        this.animationFrameId = requestAnimationFrame(render);
    }
}
